import { useMemo, useRef, useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useT } from "@/i18n";
import { getActiveLocale } from "@/i18n/active-locale";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";

const INTL_LOCALES = { cs: "cs-CZ", en: "en-GB" } as const;

/**
 * A date, chosen from a calendar rather than from the browser's own control.
 *
 * **Why not `<input type="date">`.** Its rendering is the platform's, not ours: a different
 * shape in every browser, a spinner-and-slash layout nobody designed, and on Chrome a picker
 * whose typography ignores the page entirely. It also asks for a date in *its* idea of the
 * reader's format, which for a Czech reader on an English-configured machine is a guess.
 *
 * **Why no library.** `Intl` already knows the month names, the weekday names and the order
 * they come in — in both languages this product speaks — so a picker is a month grid and two
 * buttons. Adding `react-day-picker` would mean adding its locale plumbing too, a second
 * source of "what is this month called" beside the one `lib/format.ts` already uses. The same
 * reasoning that kept an i18n framework out of a two-language product.
 *
 * Values are `yyyy-MM-dd` throughout — the wire format, never a localised string — and dates
 * are built in UTC so that no display timezone can shift a day, exactly as `formatDate` does.
 * A business date has no time and no zone; treating it as an instant is how a close date lands
 * in the wrong quarter.
 *
 * Keyboard: arrows move within the grid, PageUp/PageDown change month, Enter picks, Escape
 * closes. The trigger is a normal button, so Tab reaches it like any other control.
 */
export function DatePicker({
  value,
  onChange,
  label,
  id,
  disabled = false,
  autoFocus = false,
  onClose,
}: {
  /** `yyyy-MM-dd`, or empty for "nothing chosen yet". */
  value: string;
  onChange: (next: string) => void;
  label: string;
  id?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Called when the popover closes without a pick — lets an inline field commit or cancel. */
  onClose?: () => void;
}): React.JSX.Element {
  const t = useT();
  const locale = INTL_LOCALES[getActiveLocale()];
  const [open, setOpen] = useState(autoFocus);
  // Which month the grid is showing. Seeded from the value, or today when there is none.
  const [month, setMonth] = useState(() => startOfMonth(value ? parseISO(value) : new Date()));

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onClose?.();
      }}
    >
      <DropdownMenu.Trigger
        id={id}
        disabled={disabled}
        aria-label={label}
        className={cn(
          "border-input bg-background focus-visible:ring-ring flex h-9 w-full items-center justify-between gap-2 rounded-md border px-3 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50",
        )}
      >
        <span className={cn(!value && "text-muted-foreground")}>{value ? formatDate(value) : t("date.choose")}</span>
        <CalendarDays className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        {/* z-50: a Radix popover, which the app's z-index ladder requires above the header. */}
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="bg-popover text-popover-foreground z-50 rounded-md border p-3 shadow-md"
          // The grid handles its own arrow keys; letting Radix's menu typeahead see them would
          // move focus between "items" it thinks are a vertical list.
          onKeyDown={(event) => {
            if (event.key.startsWith("Arrow") || event.key === "PageUp" || event.key === "PageDown") {
              event.stopPropagation();
            }
          }}
        >
          <MonthGrid
            locale={locale}
            month={month}
            onMonthChange={setMonth}
            selected={value}
            onPick={(iso) => {
              onChange(iso);
              setOpen(false);
            }}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function MonthGrid({
  locale,
  month,
  onMonthChange,
  selected,
  onPick,
}: {
  locale: string;
  month: Date;
  onMonthChange: (next: Date) => void;
  selected: string;
  onPick: (iso: string) => void;
}): React.JSX.Element {
  const t = useT();
  const gridRef = useRef<HTMLDivElement>(null);

  const monthLabel = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(month),
    [locale, month],
  );

  /**
   * Weekday initials, from `Intl` rather than a hardcoded list — the whole reason this needs no
   * date library. Monday first: both languages this product speaks start their week there, and
   * `Intl.Locale`'s week info is not available everywhere yet.
   */
  const weekdays = useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
    // 2024-01-01 was a Monday, so seven days from it names every weekday in order.
    return Array.from({ length: 7 }, (_, index) => format.format(new Date(Date.UTC(2024, 0, 1 + index))));
  }, [locale]);

  const days = useMemo(() => monthDays(month), [month]);
  const todayIso = toISO(new Date());

  /** Arrow keys move by a day or a week; PageUp/PageDown by a month. */
  const onKeyDown = (event: React.KeyboardEvent, iso: string): void => {
    const deltas: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const delta = deltas[event.key];

    if (delta !== undefined) {
      event.preventDefault();
      const next = new Date(parseISO(iso).getTime() + delta * 86_400_000);
      onMonthChange(startOfMonth(next));
      // The button for the new day may not exist until the month re-renders, so focus is
      // moved after paint rather than in this handler.
      const target = toISO(next);
      requestAnimationFrame(() => {
        gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${target}"]`)?.focus();
      });
      return;
    }

    if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      onMonthChange(addMonths(month, event.key === "PageUp" ? -1 : 1));
    }
  };

  return (
    <div className="w-64">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          aria-label={t("date.previousMonth")}
          onClick={() => onMonthChange(addMonths(month, -1))}
          className="hover:bg-accent focus-visible:ring-ring rounded p-1 focus-visible:ring-2 focus-visible:outline-none"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        {/* `aria-live` so a month change is announced: the grid's contents change under a
            reader whose focus has not moved. */}
        <span aria-live="polite" className="text-sm font-medium capitalize">
          {monthLabel}
        </span>
        <button
          type="button"
          aria-label={t("date.nextMonth")}
          onClick={() => onMonthChange(addMonths(month, 1))}
          className="hover:bg-accent focus-visible:ring-ring rounded p-1 focus-visible:ring-2 focus-visible:outline-none"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="text-muted-foreground mb-1 grid grid-cols-7 gap-0.5 text-center text-[11px]">
        {weekdays.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>

      <div ref={gridRef} role="grid" aria-label={monthLabel} className="grid grid-cols-7 gap-0.5">
        {days.map(({ iso, day, outside }) => {
          const isSelected = iso === selected;
          return (
            <button
              key={iso}
              type="button"
              role="gridcell"
              data-day={iso}
              aria-selected={isSelected}
              aria-current={iso === todayIso ? "date" : undefined}
              // One tab stop for the whole grid: the selected day, or today, or the first —
              // arrows do the rest, which is how a calendar is meant to be walked.
              tabIndex={isSelected || (!selected && iso === todayIso) ? 0 : -1}
              onKeyDown={(event) => onKeyDown(event, iso)}
              onClick={() => onPick(iso)}
              className={cn(
                "focus-visible:ring-ring h-8 rounded text-sm focus-visible:ring-2 focus-visible:outline-none",
                outside && "text-muted-foreground/50",
                isSelected ? "bg-primary text-primary-foreground font-medium" : "hover:bg-accent",
                !isSelected && iso === todayIso && "ring-border ring-1",
              )}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---- Date helpers. All UTC: a business date has no time and no zone. ---- */

function parseISO(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(Date.UTC(year!, (month ?? 1) - 1, day ?? 1));
}

function toISO(date: Date): string {
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

function startOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addMonths(date: Date, delta: number): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + delta, 1));
}

/** Six weeks from the Monday on or before the first, so the grid never changes height. */
function monthDays(month: Date): { iso: string; day: number; outside: boolean }[] {
  const first = startOfMonth(month);
  // getUTCDay: 0 = Sunday. Monday-first means Sunday sits at the end of the week.
  const offset = (first.getUTCDay() + 6) % 7;
  const start = new Date(first.getTime() - offset * 86_400_000);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start.getTime() + index * 86_400_000);
    return {
      iso: toISO(date),
      day: date.getUTCDate(),
      outside: date.getUTCMonth() !== month.getUTCMonth(),
    };
  });
}
